// KpiCard — a single KPI metric card for the overview dashboard.
// White card with icon+label at top, large value in the center, small grey sub text below.
// tone="danger" renders the value in red; loading renders a skeleton placeholder.

import type { LucideIcon } from "lucide-react";

export interface KpiCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: LucideIcon;
  tone?: "default" | "danger" | "success";
  loading?: boolean;
  className?: string;
}

const VALUE_COLOR: Record<NonNullable<KpiCardProps["tone"]>, string> = {
  default: "text-grey-900",
  danger: "text-red-bright",
  success: "text-green-deep",
};

export function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  tone = "default",
  loading = false,
  className = "",
}: KpiCardProps) {
  const valueColor = VALUE_COLOR[tone];

  return (
    <div className={`min-w-0 rounded-card border border-grey-200 bg-white p-4 shadow-card sm:p-5 ${className}`}>
      {/* Icon + label row */}
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[8px] bg-grey-100 text-grey-500">
          <Icon size={16} strokeWidth={1.75} />
        </span>
        <span className="min-w-0 text-[12.5px] font-medium leading-tight text-grey-500">{label}</span>
      </div>

      {/* Value */}
      {loading ? (
        <div className="mb-2 h-7 w-28 animate-pulse rounded-[6px] bg-grey-100" />
      ) : (
        <p className={`mb-1 text-[22px] font-bold leading-none tracking-tight sm:text-[26px] ${valueColor}`}>{value}</p>
      )}

      {/* Sub text */}
      {loading ? (
        <div className="h-3 w-20 animate-pulse rounded-[4px] bg-grey-100" />
      ) : sub ? (
        <p className="text-[12px] text-grey-400">{sub}</p>
      ) : null}
    </div>
  );
}
